/**
 * Thin section navigation for Forms Manager, styled to match the clinician
 * visit page sidebar (`OASISNavigation`). Each tab shows the section's layout
 * and consistency badges and a three-dot menu of read-only section actions.
 */
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import { Clipboard, FileJson, FileText, MoreHorizontal } from 'lucide-react';
import type { SectionLayoutState } from './formSchemaTypes';
import { SectionConsistencyBadge } from './SectionConsistencyBadge';
import { SectionLayoutStatusBadge } from './SectionLayoutStatusBadge';
import type { SectionConsistency } from './sectionConsistency';

interface FormSchemaSectionNavProps {
  /** Section ids in display order. */
  sections: string[];
  /** Resolves a section id to its user-facing label. */
  labelFor: (sectionId: string) => string;
  selectedSection: string | null;
  onSelect: (sectionId: string) => void;
  getLayoutState: (sectionId: string) => SectionLayoutState;
  /**
   * Cross-form design consistency for the section. Separate from
   * {@link getLayoutState}, which reports whether a layout exists and is
   * valid, not whether it matches the other forms sharing its questions.
   * Omit to hide the marker entirely.
   */
  getConsistency?: (sectionId: string) => SectionConsistency;
  onCopyLayout: (sectionId: string) => void;
  onViewQuestions: (sectionId: string) => void;
  onOpenLayoutJson: (sectionId: string) => void;
}

export function FormSchemaSectionNav({
  sections,
  labelFor,
  selectedSection,
  onSelect,
  getLayoutState,
  getConsistency,
  onCopyLayout,
  onViewQuestions,
  onOpenLayoutJson,
}: FormSchemaSectionNavProps) {
  return (
    <div className='w-full flex-shrink-0 border-b border-border pb-3 md:w-48 md:border-b-0 md:border-r md:pb-0 md:pr-3'>
      <div className='space-y-0.5'>
        <span className='mb-1 block px-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
          Sections
        </span>
        {sections.map(section => {
          const isActive = selectedSection === section;
          const layoutState = getLayoutState(section);
          const consistency = getConsistency?.(section);
          const hasLayout = layoutState.kind !== 'missing';
          return (
            <div
              key={section}
              className={`group flex items-center gap-1 rounded-md pr-0.5 ${
                isActive
                  ? 'bg-primary/10'
                  : 'hover:bg-muted focus-within:bg-muted'
              }`}
            >
              <button
                type='button'
                onClick={() => onSelect(section)}
                className={`flex min-w-0 flex-1 items-center justify-between gap-2 px-2.5 py-2 text-left text-xs leading-snug transition-colors ${
                  isActive
                    ? 'font-semibold text-primary'
                    : 'text-muted-foreground group-hover:text-foreground'
                }`}
              >
                <span className='min-w-0 flex-1 truncate'>
                  {labelFor(section)}
                </span>
                <span className='flex shrink-0 items-center gap-1'>
                  <SectionLayoutStatusBadge state={layoutState} />
                  {consistency ? (
                    <SectionConsistencyBadge state={consistency} />
                  ) : null}
                </span>
              </button>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='h-6 w-6 shrink-0 p-0 opacity-60 hover:opacity-100'
                    aria-label={`Actions for ${labelFor(section)}`}
                    onClick={() => onSelect(section)}
                  >
                    <MoreHorizontal className='h-3.5 w-3.5' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <DropdownMenuItem
                    onClick={() => onCopyLayout(section)}
                    disabled={!hasLayout}
                  >
                    <Clipboard className='h-3.5 w-3.5 mr-2' />
                    Copy layout
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onViewQuestions(section)}>
                    <FileText className='h-3.5 w-3.5 mr-2' />
                    View questions
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onOpenLayoutJson(section)}
                    disabled={!hasLayout}
                  >
                    <FileJson className='h-3.5 w-3.5 mr-2' />
                    Layout JSON…
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        })}
      </div>
    </div>
  );
}
